import normalizeTravelMode from 'features/Responder/features/Tours/features/TourBuilder/utils/normalizeTravelMode';
import {
  getDuplicateNameMessage,
  hasDuplicateEntityName,
} from 'features/Responder/utils/entityNameValidation';
import {
  saveTourRoute,
  updateTourRoute,
} from 'features/Responder/features/Tours/services/tourApi.js';
import { useCallback, useRef } from 'react';

/**
 * Persist routes to backend with common validation.
 * @param {object} params
 * @param {boolean} params.isLooped
 */
export function useRoutePersistence({
  canSaveRoute,
  editingTourId,
  routeWaypointOrder,
  checkpointMetaById,
  stopMetaById,
  routeData,
  builderTourData,
  activeTourData,
  tours,
  addTour,
  resetBuilder,
  setSaveError,
  setSaveSuccess,
  setSaving,
  onPersistSuccess,
  isLooped = false,
}) {
  const isSavingRef = useRef(false);
  const handleSaveRoute = useCallback(
    async (mode = 'new') => {
      const isPersistedNodeId = (value) => {
        if (typeof value === 'number') return Number.isFinite(value);
        if (typeof value === 'string' && value.trim().length) {
          return /^-?\d+$/.test(value.trim());
        }
        return false;
      };

      const normalizeNodeIdForMode = (id) => {
        if (!isPersistedNodeId(id)) return null;
        if (mode !== 'update') return id;
        return typeof id === 'string' ? Number(id) : id;
      };

      if (isSavingRef.current) return;
      const SAVE_TIMEOUT_MS = 20000;
      let didComplete = false;
      let timeoutId;
      setSaveError('');
      setSaveSuccess('');
      if (!canSaveRoute) {
        setSaveError('Build a route with a valid start and end before saving.');
        return;
      }
      const tourName = `${builderTourData?.name ?? activeTourData?.name ?? ''}`.trim();
      if (!tourName) {
        setSaveError('Enter a tour name before saving.');
        return;
      }
      if (hasDuplicateEntityName(tours, tourName, mode === 'update' ? editingTourId : null)) {
        setSaveError(getDuplicateNameMessage('Tour'));
        return;
      }
      if (mode === 'update' && !editingTourId) {
        setSaveError('Select a tour to update first.');
        return;
      }
      isSavingRef.current = true;
      setSaving(true);
      timeoutId = setTimeout(() => {
        if (didComplete) return;
        setSaveError('Save is taking longer than expected. Please try again.');
        setSaving(false);
        isSavingRef.current = false;
      }, SAVE_TIMEOUT_MS);
      try {
        const toTravelNode = (node, idx, type, legSeconds = 0, totalSeconds = 0) => {
          const geometry =
            node.geometry ||
            (node.geometryType || node.shapeMode
              ? { type: node.geometryType || node.shapeMode }
              : null);
          const zoneId = node.zoneId ?? node.zone_id ?? geometry?.zoneId ?? null;
          const fallbackRadiusMeters = type === 'origin' ? 25 : 100;
          const normalizedId = normalizeNodeIdForMode(node?.id);
          return {
            ...(normalizedId != null ? { id: normalizedId } : {}),
            name: node.displayName || node.label || `Checkpoint ${idx + 1}`,
            coords: [Number(node.lat), Number(node.lng)],
            type,
            mode: normalizeTravelMode(node.mode || node.travelMode),
            address: node.address || '',
            radius: Number(node.radiusMeters ?? fallbackRadiusMeters),
            geometry,
            ...(zoneId != null ? { zoneId } : {}),
            waitTime:
              type === 'origin'
                ? null
                : (() => {
                    const val = Number(node.stopTimeMinutes);
                    return Number.isFinite(val) && val > 0 ? val : null;
                  })(),
            segmentTime:
              type === 'origin'
                ? null
                : (() => {
                    const minutes = Math.max(0, Math.round((legSeconds || 0) / 60));
                    return minutes > 0 ? minutes : null;
                  })(),
            bonusTime:
              type === 'origin'
                ? null
                : (() => {
                    const seconds =
                      node.timeWindowSeconds ||
                      (Number.isFinite(Number(node.timeWindowMinutes))
                        ? Number(node.timeWindowMinutes) * 60
                        : 0);
                    const minutes = Math.max(0, Math.round(seconds / 60));
                    return minutes > 0 ? minutes : null;
                  })(),
            totalTimeFromStart:
              type === 'origin'
                ? null
                : (() => {
                    const minutes = Math.max(0, Math.round((totalSeconds || 0) / 60));
                    return minutes > 0 ? minutes : null;
                  })(),
          };
        };

        const parseDurationSeconds = (val) => {
          if (typeof val === 'number') return val;
          if (typeof val === 'string') {
            const n = parseFloat(val.replace('s', ''));
            return Number.isFinite(n) ? n : 0;
          }
          return 0;
        };

        const nodes = [];
        const existingNodes =
          (Array.isArray(builderTourData?.nodes) && builderTourData.nodes) ||
          (Array.isArray(activeTourData?.nodes) && activeTourData.nodes) ||
          [];
        const existingLoopDestinationId =
          existingNodes.find((node) => `${node?.type ?? ''}`.toLowerCase().trim() === 'destination')
            ?.id ?? null;
        const existingLoopDestinationNode =
          existingNodes.find(
            (node) => `${node?.type ?? ''}`.toLowerCase().trim() === 'destination',
          ) || null;

        routeWaypointOrder.forEach((item, idx) => {
          if (item.type === 'checkpoint') {
            const meta = checkpointMetaById[item.id] || {};
            nodes.push(
              toTravelNode(
                {
                  ...item.ref,
                  lat: item.ref?.lat,
                  lng: item.ref?.lng,
                  address: item.ref?.address,
                  radiusMeters: item.ref?.radiusMeters,
                  timeWindowSeconds: item.ref?.timeWindowSeconds,
                  geometry: item.ref?.geometry,
                },
                nodes.length,
                'checkpoint',
                meta.legSeconds,
                meta.tripTotalSeconds,
              ),
            );
          } else {
            const meta = stopMetaById[item.id] || {};
            const isLastInOrder = idx === routeWaypointOrder.length - 1;
            const isDestination = isLastInOrder && !isLooped;
            const nodeType =
              item.type === 'origin' ? 'origin' : isDestination ? 'destination' : 'checkpoint';
            nodes.push(
              toTravelNode(
                {
                  ...item.ref,
                  lat: item.ref?.lat,
                  lng: item.ref?.lng,
                  address: item.ref?.address,
                  stopTimeMinutes: item.ref?.stopTimeMinutes,
                  timeWindowMinutes: item.ref?.timeWindowMinutes,
                  geometry: item.ref?.geometry,
                },
                nodes.length,
                nodeType,
                meta.legSeconds,
                meta.tripTotalSeconds,
              ),
            );
          }
        });

        const legs = Array.isArray(routeData?.legs) ? routeData.legs : [];
        if (isLooped && routeWaypointOrder.length) {
          const originItem =
            routeWaypointOrder.find((item) => item.type === 'origin') ||
            routeWaypointOrder.find((item) => item.type === 'stop');
          if (originItem?.ref?.lat != null && originItem?.ref?.lng != null && legs.length) {
            const loopLegSeconds = parseDurationSeconds(legs[legs.length - 1]?.duration);
            const totalLegSeconds = legs.reduce(
              (sum, leg) => sum + parseDurationSeconds(leg?.duration),
              0,
            );
            const loopRadiusMeters =
              Number(originItem.ref?.loopDestinationRadiusMeters) > 0
                ? Number(originItem.ref?.loopDestinationRadiusMeters)
                : Number(originItem.ref?.returnRadiusMeters) > 0
                  ? Number(originItem.ref?.returnRadiusMeters)
                  : Number(originItem.ref?.radiusMeters) > 0
                    ? Number(originItem.ref?.radiusMeters)
                    : Number(existingLoopDestinationNode?.radiusMeters) > 0
                      ? Number(existingLoopDestinationNode?.radiusMeters)
                      : Number(existingLoopDestinationNode?.radius) > 0
                        ? Number(existingLoopDestinationNode?.radius)
                        : undefined;
            const loopHandleBearing = Number.isFinite(
              Number(originItem.ref?.loopDestinationHandleBearing),
            )
              ? Number(originItem.ref?.loopDestinationHandleBearing)
              : Number.isFinite(Number(originItem.ref?.returnHandleBearing))
                ? Number(originItem.ref?.returnHandleBearing)
                : Number.isFinite(Number(originItem.ref?.handleBearing))
                  ? Number(originItem.ref?.handleBearing)
                  : Number.isFinite(Number(existingLoopDestinationNode?.handleBearing))
                    ? Number(existingLoopDestinationNode?.handleBearing)
                    : undefined;
            const existingLoopGeometry =
              existingLoopDestinationNode?.geometry || originItem.ref?.geometry;
            const loopGeometry =
              existingLoopGeometry || Number.isFinite(loopRadiusMeters)
                ? {
                    ...(existingLoopGeometry || {}),
                    type: existingLoopGeometry?.type || 'circle',
                    ...(Number.isFinite(loopRadiusMeters)
                      ? { radiusMeters: loopRadiusMeters }
                      : {}),
                    ...(Number.isFinite(loopHandleBearing)
                      ? {
                          handleBearing: loopHandleBearing,
                          angleRad: loopHandleBearing,
                        }
                      : {}),
                  }
                : undefined;
            const loopRef = {
              ...originItem.ref,
              // Never reuse origin id for loop-return destination.
              // Reuse persisted destination id when available during updates.
              id:
                mode === 'update' && isPersistedNodeId(existingLoopDestinationId)
                  ? existingLoopDestinationId
                  : null,
              label:
                originItem.ref?.label ||
                originItem.ref?.address ||
                originItem.ref?.displayName ||
                'Start',
              mode: normalizeTravelMode(originItem.ref?.returnMode || originItem.ref?.mode),
              radiusMeters: loopRadiusMeters,
              geometry: loopGeometry,
              stopTimeMinutes: 0,
              timeWindowMinutes: 0,
              timeWindowSeconds: 0,
            };
            nodes.push(
              toTravelNode(loopRef, nodes.length, 'destination', loopLegSeconds, totalLegSeconds),
            );
          }
        }
        const nodesWithLegs = nodes.map((node, idx) => {
          const legIdx = idx - 1;
          if (legIdx < 0) return { ...node };
          const rawLeg = legs[legIdx] || {};
          const {
            mode: legMode,
            travelMode: legTravelMode,
            duration: legDuration,
            distanceMeters,
            polyline,
            encodedPolyline,
            ...restLeg
          } = rawLeg;
          return {
            ...node,
            leg: {
              distanceMeters: Number.isFinite(distanceMeters) ? distanceMeters : null,
              durationSeconds: parseDurationSeconds(legDuration),
              encodedPolyline: encodedPolyline || polyline || null,
              travelMode:
                (legTravelMode || legMode || 'DRIVING').toUpperCase() === 'WALKING'
                  ? 'WALKING'
                  : 'DRIVING',
              ...restLeg,
            },
          };
        });

        const payload = {
          name: tourName,
          nodes: nodesWithLegs,
          encodedPolyline: routeData?.polyline || ''.toString(),
          routeOptimization: 'fastest',
        };

        const persistedCheckpoints = routeWaypointOrder
          .filter((item) => item?.type !== 'origin')
          .map((item, idx) => ({
            ...(item?.ref || {}),
            id: item?.ref?.id ?? `cp-${idx}`,
            type: 'checkpoint',
            kind: 'checkpoint',
          }));

        const result =
          mode === 'update'
            ? await updateTourRoute(editingTourId, payload)
            : await saveTourRoute(payload);
        const rawSavedTour = result?.tour || result?.trip || result;
        // Keep local editor/list state anchored to the current builder waypoints.
        // Some update responses can include stale/appended node arrays.
        const persistedNodes = nodesWithLegs;
        const savedTour =
          rawSavedTour && typeof rawSavedTour === 'object'
            ? {
                ...rawSavedTour,
                name: rawSavedTour?.name || payload?.name || tourName,
                nodes: persistedNodes,
                checkpoints: persistedCheckpoints,
                stops: persistedCheckpoints,
                legs:
                  Array.isArray(rawSavedTour?.legs) && rawSavedTour.legs.length
                    ? rawSavedTour.legs
                    : Array.isArray(routeData?.legs)
                      ? routeData.legs
                      : [],
                encodedPolyline:
                  rawSavedTour?.encodedPolyline ||
                  rawSavedTour?.polyline ||
                  payload?.encodedPolyline ||
                  '',
                totalDuration: rawSavedTour?.totalDuration ?? routeData?.totalDuration ?? null,
                totalDistanceMeters:
                  rawSavedTour?.totalDistanceMeters ?? routeData?.totalDistanceMeters ?? null,
                isLooped:
                  typeof rawSavedTour?.isLooped === 'boolean' ? rawSavedTour.isLooped : isLooped,
              }
            : null;
        // For update mode the backend may return a minimal response without an id.
        // Fall back to editingTourId so the in-memory list always stays current.
        const effectiveSavedTour =
          savedTour?.id != null
            ? savedTour
            : mode === 'update' && editingTourId != null
              ? {
                  ...(savedTour || {}),
                  id: editingTourId,
                  name: savedTour?.name || tourName,
                  nodes: savedTour?.nodes || persistedNodes,
                  checkpoints: savedTour?.checkpoints || persistedCheckpoints,
                  stops: savedTour?.stops || persistedCheckpoints,
                  encodedPolyline: savedTour?.encodedPolyline || payload?.encodedPolyline || '',
                  isLooped,
                }
              : savedTour;
        if (effectiveSavedTour?.id != null && typeof addTour === 'function') {
          // Keep TourContext list in sync immediately; addTour is an upsert by id.
          addTour(effectiveSavedTour);
        }
        setSaveError('');
        setSaveSuccess(
          mode === 'update' ? 'tour updated successfully.' : 'tour saved successfully.',
        );

        resetBuilder();
        if (typeof onPersistSuccess === 'function') {
          onPersistSuccess({ mode, savedTour: effectiveSavedTour });
        }
      } catch (err) {
        setSaveError(err.message || 'Failed to save tour');
      } finally {
        didComplete = true;
        if (timeoutId) clearTimeout(timeoutId);
        setSaving(false);
        isSavingRef.current = false;
      }
    },
    [
      activeTourData?.name,
      activeTourData?.nodes,
      tours,
      builderTourData?.name,
      builderTourData?.nodes,
      canSaveRoute,
      checkpointMetaById,
      editingTourId,
      routeData?.legs,
      routeData?.polyline,
      routeWaypointOrder,
      stopMetaById,
      resetBuilder,
      setSaveError,
      setSaveSuccess,
      setSaving,
      addTour,
      onPersistSuccess,
      isLooped,
    ],
  );

  return { handleSaveRoute };
}
